const ACCESS_MODES = Object.freeze({
  FULL: "full",
  GRACE: "grace",
  LANDING_ONLY: "landing_only",
  SUSPENDED: "suspended",
});

function validDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function daysUntil(value, now = new Date()) {
  const date = validDate(value);
  if (!date) return null;
  return Math.max(0, Math.ceil((date.getTime() - now.getTime()) / 86400000));
}

export function getProjectBillingAccess(project, now = new Date()) {
  const billing = project?.billing || {};
  const billingStatus = String(billing.status || "trial").toLowerCase();
  const projectStatus = String(project?.status || "active").toLowerCase();
  const explicitMode = String(billing.accessMode || "").toLowerCase();
  const graceUntil = validDate(billing.graceUntil);
  const trialStartedAt = validDate(billing.trialStartedAt);
  const trialEndsAt = validDate(billing.trialEndsAt);
  const trialExpired = billingStatus === "trial" && trialEndsAt && now > trialEndsAt;

  let mode = ACCESS_MODES.FULL;
  let reason = billingStatus === "trial" ? "trial_active" : "billing_active";

  if (projectStatus === "cancelled") {
    mode = ACCESS_MODES.SUSPENDED;
    reason = "project_cancelled";
  } else if (projectStatus === "paused" && billing.pausedByBilling !== true) {
    mode = ACCESS_MODES.SUSPENDED;
    reason = "project_paused_by_admin";
  } else if (trialExpired) {
    mode = ACCESS_MODES.LANDING_ONLY;
    reason = "trial_expired";
  } else if (billingStatus === "past_due") {
    if (graceUntil && now <= graceUntil) {
      mode = ACCESS_MODES.GRACE;
      reason = "payment_grace_period";
    } else {
      mode = ACCESS_MODES.LANDING_ONLY;
      reason = "payment_grace_expired";
    }
  } else if (["paused", "cancelled"].includes(billingStatus)) {
    mode = ACCESS_MODES.LANDING_ONLY;
    reason = billingStatus === "cancelled"
      ? "subscription_cancelled"
      : "subscription_paused";
  } else if (explicitMode === ACCESS_MODES.LANDING_ONLY) {
    mode = ACCESS_MODES.LANDING_ONLY;
    reason = "billing_restricted";
  } else if (explicitMode === ACCESS_MODES.GRACE) {
    mode = ACCESS_MODES.GRACE;
    reason = "payment_grace_period";
  } else if (explicitMode === ACCESS_MODES.SUSPENDED) {
    mode = ACCESS_MODES.SUSPENDED;
    reason = "project_suspended";
  }

  return {
    mode,
    reason,
    billingStatus,
    projectStatus,
    graceUntil,
    trialStartedAt,
    trialEndsAt,
    trialDaysRemaining: billingStatus === "trial" ? daysUntil(trialEndsAt, now) : null,
    trialExpired: Boolean(trialExpired),
    restrictedAt: validDate(billing.restrictedAt),
    daysRemaining: daysUntil(graceUntil, now),
    isTrial: billingStatus === "trial",
    isFull: mode === ACCESS_MODES.FULL,
    isGrace: mode === ACCESS_MODES.GRACE,
    isLandingOnly: mode === ACCESS_MODES.LANDING_ONLY,
    isSuspended: mode === ACCESS_MODES.SUSPENDED,
    isOperational: [ACCESS_MODES.FULL, ACCESS_MODES.GRACE].includes(mode),
    needsPaymentAction: mode === ACCESS_MODES.GRACE || mode === ACCESS_MODES.LANDING_ONLY,
  };
}

export function billingAccessLabel(access) {
  if (access?.isTrial && !access?.trialExpired) {
    const days = access.trialDaysRemaining;
    return days === 1 ? "Prueba gratis · 1 día" : `Prueba gratis · ${days ?? 30} días`;
  }
  if (access?.reason === "trial_expired") return "Prueba gratis finalizada";
  if (access?.isGrace) return "Pago pendiente · período de gracia";
  if (access?.isLandingOnly) return "Funciones pausadas por pago";
  if (access?.isSuspended) return "Proyecto suspendido";
  return "Servicio activo";
}

export { ACCESS_MODES };
